<template>
    <div class="co-checkout">

        <!-- Skeleton -->
        <template v-if="initLoading">
            <div class="card co-skeleton">
                <div class="co-skel-line co-skel-line--title"></div>
                <div class="co-skel-line"></div>
                <div class="co-skel-line"></div>
                <div class="co-skel-line"></div>
                <div class="co-skel-line co-skel-line--short"></div>
            </div>
            <div class="co-skel-btn"></div>
        </template>

        <!-- Already owned -->
        <template v-else-if="isOwned">
            <div class="card co-owned">
                <div class="co-owned-icon">✓</div>
                <div class="co-owned-title">You already own this</div>
                <div class="co-owned-sub">No need to pay again — your access is active.</div>
                <a :href="accURL + '/dashboard'" class="btn-primary co-owned-btn">
                    Go to your profile →
                </a>
            </div>
        </template>

        <!-- Pass-only: нельзя купить (нет подписки / мало кредитов) -->
        <template v-else-if="isPassBlocked">
            <div class="card co-pass">

                <div class="co-pass-hero">
                    <div class="co-pass-lock">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    </div>
                    <h2 class="co-pass-title">
                        <template v-if="passState === 'need-pass'">This product is <em>Pass only.</em></template>
                        <template v-else>Not enough credits</template>
                    </h2>
                    <p class="co-pass-sub">
                        <template v-if="passState === 'need-pass'">Unlock it with credits from an active Ecomzy Pass.</template>
                        <template v-else>You have {{ creditsBalance }} credits — this needs {{ cost }}.</template>
                    </p>
                </div>

                <div class="co-pass-body">
                    <div v-if="passState === 'need-pass'" class="co-pass-perks">
                        <div class="co-pass-perk"><span class="co-pass-check">✓</span> Monthly credits to unlock any Pass-only product</div>
                        <div class="co-pass-perk"><span class="co-pass-check">✓</span> Full access to every Pass-exclusive product</div>
                        <div class="co-pass-perk"><span class="co-pass-check">✓</span> Cancel anytime · 30-day money-back</div>
                    </div>

                    <p v-else-if="passState === 'low-max'" class="co-pass-note">
                        Your credits will refresh at the start of the next billing cycle.
                    </p>

                    <a
                        v-if="passState !== 'low-max'"
                        :href="baseURL + '/pricing'"
                        class="co-pass-btn"
                    >
                        {{ passState === 'need-pass' ? 'See plans' : 'Upgrade plan' }} →
                    </a>
                </div>
            </div>
        </template>

        <!-- Form -->
        <template v-else>

            <!-- Contact details - только для гостей (email). Address собирает Stripe. -->
            <div class="card" v-if="!isAuth">
                <div class="section-title" style="margin-bottom:14px">Contact details</div>

                <div class="fg">
                    <input
                        v-model="form.email"
                        @blur="checkEmail"
                        class="fi"
                        :class="{ 'has-error': errors.email }"
                        type="email"
                        id="sub-email"
                        placeholder="Email"
                        autocomplete="email"
                    >
                    <label class="fl" for="sub-email">Email<span class="req">*</span></label>
                </div>

                <div v-if="emailHasProduct" class="sub-email-notice">
                    This product has already been purchased with this email.
                    <a :href="accountUrl + '/login'">Log in to access it. →</a>
                </div>
            </div>

            <!-- Order summary с credits discount -->
            <div v-if="credits.discount_cents > 0" class="card co-order-summary">
                <div class="section-title" style="margin-bottom:14px">Order summary</div>
                <div class="co-row">
                    <span class="co-row__label">Subtotal</span>
                    <span class="co-row__value">{{ formatCents( price ) }}</span>
                </div>
                <div class="co-row co-row--discount">
                    <span class="co-row__label">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                            <line x1="7" y1="7" x2="7.01" y2="7"/>
                        </svg>
                        Credits applied
                    </span>
                    <span class="co-row__value">−{{ formatCents( credits.discount_cents ) }}</span>
                </div>
                <div class="co-row co-row--total">
                    <span class="co-row__label">Total to pay</span>
                    <span class="co-row__value">{{ formatCents( credits.total_cents ) }}</span>
                </div>
            </div>

            <!-- Payment - dynamic subcomponent per gateway -->
            <component
                :is="paymentComponent"
                v-if="paymentComponent && publicKey && ! isFullyCovered"
                ref="payment"
                :public-key="publicKey"
                :saved-cards="savedCards"
                :amount="price"
                :order-payload="orderPayload"
                :contact="contactPayload"
                :is-auth="isAuth"
                @error="onPaymentError"
                @loading="onPaymentLoading"
                @credits-changed="handleCreditsChanged"
            ></component>

            <!-- CTA -->
            <div class="cta-wrap">
                <button
                    class="btn-primary"
                    :disabled="isLoading || ( ! paymentComponent && ! isFullyCovered )"
                    @click="proceedToPayment"
                >
                    <span v-if="isLoading" class="co-spinner"></span>
                    <span v-else>
                        <template v-if="isFullyCovered">Apply credits</template>
                        <template v-else>Pay {{ formatPrice }}</template>
                    </span>
                </button>

                <div v-if="globalError" class="co-error">{{ globalError }}</div>

                <!-- Trust -->
                <div class="trust">
                    <div class="trust-line">Secure payment · Instant delivery</div>
                    <div class="trust-bottom">
                        <div class="tb">
                            <div class="tb-icon">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g opacity="0.5">
                                        <path d="M17 11H7C5.89543 11 5 11.8954 5 13V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V13C19 11.8954 18.1046 11 17 11Z" fill="#E36E00" fill-opacity="0.1" stroke="#E36E00" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M8 11V7C8 5.93913 8.42143 4.92172 9.17157 4.17157C9.92172 3.42143 10.9391 3 12 3C13.0609 3 14.0783 3.42143 14.8284 4.17157C15.5786 4.92172 16 5.93913 16 7V11" stroke="#E36E00" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M12 17.5C12.8284 17.5 13.5 16.8284 13.5 16C13.5 15.1716 12.8284 14.5 12 14.5C11.1716 14.5 10.5 15.1716 10.5 16C10.5 16.8284 11.1716 17.5 12 17.5Z" fill="#E36E00" fill-opacity="0.4"/>
                                    </g>
                                </svg>
                            </div>
                            <div>
                                <div class="tb-title">SSL Secure payment</div>
                                <div class="tb-sub">256-bit encryption</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </template>

    </div>
</template>

<script>
import PaymentStripe from './PaymentStripe.vue';
import { track, trackEcommerce, gtmItem, GTM_EVENTS } from '../../js/utils/gtm';

export default {
    name: 'CheckoutApp',

    components: {
        PaymentStripe,
    },

    props: {
        productId      : { type: Number,  required: true },
        productSlug    : { type: String,  required: true },
        productName    : { type: String,  default: '' },
        price          : { type: Number,  default: 0 },
        isAuth         : { type: Boolean, default: false },
        userEmail      : { type: String,  default: '' },
        userName       : { type: String,  default: '' },
        accessType     : { type: String,  default: '' },
        hasPass        : { type: Boolean, default: false },
        isMax          : { type: Boolean, default: false },
        creditsBalance : { type: Number,  default: 0 },
        cost           : { type: Number,  default: 0 },
    },

    data() {
        return {
            initLoading : true,
            isOwned     : false,
            baseURL     : window.baseURL || '',
            accURL      : window.accURL  || '',
            accountUrl  : window.accURL  || '',
            emailHasProduct : false,

            credits: {
                balance_cents  : 0,
                discount_cents : 0,
                total_cents    : 0,
            },

            orderPayload: {
                slugs: [ this.productSlug ],
                discount_cents: 0   // синхронизируется с credits.discount_cents после init
            },

            form: {
                first_name  : '',
                last_name   : '',
                email       : '',
                phone       : '',
                country     : 'US',
                address     : '',
                address2    : '',
                city        : '',
                state       : '',
                postal_code : '',
            },

            errors      : {},
            globalError : '',
            isLoading   : false,

            // From init response
            publicKey   : '',
            savedCards  : [],
            gateway     : '',
        };
    },

    computed: {

        isPassOnly() {
            return this.accessType === 'pass_only';
        },
        passState() {
            if( ! this.isPassOnly )                 return 'normal';
            if( ! this.hasPass )                    return 'need-pass';
            if( this.creditsBalance >= this.cost )  return 'enough';
            if( this.isMax )                        return 'low-max';
            return 'low-upgrade';
        },
        isPassBlocked() {
            return [ 'need-pass', 'low-upgrade', 'low-max' ].includes( this.passState );
        },

        // Динамический subcomponent по типу gateway из init
        paymentComponent() {

            if( this.gateway === 'stripe' )
                return 'PaymentStripe';

            // future: 'PaymentPayPal', 'PaymentSellvia' и т.д.

            return null;
        },

        // Для auth не отправляем contact - бэк возьмёт email из $this->auth
        contactPayload() {
            return this.form;
        },

        isFullyCovered() {
            return this.credits.discount_cents > 0 && this.credits.total_cents === 0;
        },

        formatPrice() {
            const cents = this.credits.total_cents || this.price;
            return '$' + ( cents / 100 ).toFixed( 2 );
        },
    },

    mounted() {
        this.init();
        window.addEventListener( 'pageshow', this.resetLoader );
    },

    beforeUnmount() {
        window.removeEventListener( 'pageshow', this.resetLoader );
    },

    methods: {

        formatCents( cents ) {
            return '$' + ( ( cents || 0 ) / 100 ).toFixed( 2 );
        },

        resetLoader( event ) {
            if( event.persisted )
                this.isLoading = false;
        },

        async checkEmail() {

            const email = this.form.email?.trim();

            // Пустой / невалидный / auth - не проверяем
            if( this.isAuth || ! email || ! /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( email ) ) {
                this.emailHasProduct = false;
                return;
            }

            this.emailChecking = true;

            try {
                const response = await this.$http.post( '/checkout/checkEmail', { email, slugs: [ this.productSlug ] } );
                const { status, data } = response?.data || {};

                if( status === 'success' ) {
                    this.emailHasProduct = data.has_product || false;
                    if( data.account_url )
                        this.accountUrl = data.account_url;
                }
            } catch( e ) {
                this.emailHasProduct = false;   // fail-open: intent отловит case 2 как 409 backup
            } finally {
                this.emailChecking = false;
            }
        },

        // WP1 канон-айтем продукта. discount = покрыто кредитами (гибрид F-05),
        // чтобы price - discount = value.
        productItem() {
            const discountUsd = +( ( this.credits?.discount_cents || 0 ) / 100 ).toFixed( 2 );
            return gtmItem( {
                id       : this.productSlug,
                name     : this.productName,
                category : 'product',
                price    : +( this.price / 100 ).toFixed( 2 ),
                discount : discountUsd > 0 ? discountUsd : undefined,
            } );
        },

        async init() {

            try {
                const response = await this.$http.post('/checkout/init', {
                    slugs: [ this.productSlug ],
                });
                const { status, data } = response?.data || {};
                if( status !== 'success' )
                    throw new Error( data?.message || 'Init failed' );

                const item = data.items?.find( i => i.slug === this.productSlug );
                this.isOwned = item?.is_owned || false;

                if( this.isOwned ) {
                    setTimeout(() => this.initLoading = false, 500);
                    return;
                }

                // Prefill только для гостей
                if( data.user ) {

                    // Email / country / postal_code - prefill для всех (для Stripe Element + Link)
                    this.form.email       = data.user.email       || '';
                    this.form.country     = data.user.country     || 'US';
                    this.form.postal_code = data.user.postal_code || '';
                    this.form.phone       = data.user.phone       || '';

                    // Остальные адресные поля - только для гостей (auth их не вводит, контактная форма скрыта)
                    if( ! this.isAuth ) {

                        const nameParts = ( data.user.name || '' ).trim().split(' ');

                        this.form.first_name = nameParts[0] || '';
                        this.form.last_name  = nameParts.slice(1).join(' ') || '';
                        this.form.address    = data.user.address  || '';
                        this.form.address2   = data.user.address2 || '';
                        this.form.city       = data.user.city     || '';
                        this.form.state      = data.user.state    || '';
                    }
                }

                this.publicKey  = data.stripe_pub_key || '';
                this.savedCards = data.saved_cards || [];
                this.gateway    = data.gateway || '';
                this.credits    = data.credits || {
                    balance_cents  : 0,
                    discount_cents : 0,
                    total_cents    : this.price,
                };

                // Бэкенд (/checkout/intent) сверяет присланный discount_cents
                // с пересчитанным - синхронизируем с актуальным из init.
                this.orderPayload.discount_cents = this.credits.discount_cents || 0;

                // ③ begin_checkout - на открытии страницы /checkout (не по клику CTA
                // на product page). Только когда покупка реально доступна.
                if( ! this.isPassBlocked ) {
                    trackEcommerce( GTM_EVENTS.BEGIN_CHECKOUT, {
                        currency : 'USD',
                        value    : +( ( this.credits.total_cents ?? this.price ) / 100 ).toFixed( 2 ),
                        items    : [ this.productItem() ],
                    }, {
                        payment_type : this.isFullyCovered ? 'credits' : 'card',
                    } );

                    // credits_unlock_started - вход воронки кредитной разблокировки.
                    // Шлём здесь (а не на product page), т.к. на /checkout сходятся ВСЕ пути
                    // (pass_only / instant / прямой заход) - иначе started терялся (F-20).
                    // Только при полном покрытии кредитами (завершение = credits_unlock_completed на success).
                    if( this.isFullyCovered ) {
                        const creditsSpent = Math.round( ( this.credits.discount_cents || 0 ) / 100 );
                        track( GTM_EVENTS.CREDITS_UNLOCK_STARTED, {
                            product_id            : String( this.productId ),
                            credits_spent         : creditsSpent,
                            credits_balance_after : Math.max( 0, this.creditsBalance - creditsSpent ),
                        } );
                    }
                }

            } catch (e) {
                console.error('Checkout init failed:', e);
            } finally {
                setTimeout( () => this.initLoading = false, 500 );
            }
        },

        async proceedToPayment() {

            // Validate contact только для гостей
            if( ! this.isAuth && ! this.validate() )
                return;

            this.globalError = '';

            // add_payment_info - пользователь подтвердил оплату (сабмит формы).
            // begin_checkout шлётся на init; view_item/pass_gate - на product page.
            trackEcommerce( GTM_EVENTS.ADD_PAYMENT_INFO, {
                currency : 'USD',
                value    : +( ( this.credits.total_cents ?? this.price ) / 100 ).toFixed( 2 ),
                items    : [ this.productItem() ],
            }, {
                payment_type : this.isFullyCovered ? 'credits' : 'card',
            } );

            if( this.isFullyCovered ) {
                await this.submitCreditsOnly();
            } else {
                if( this.$refs.payment?.submit )
                    await this.$refs.payment.submit();
            }
        },

        async submitCreditsOnly() {

            this.isLoading = true;

            try {

                const response = await this.$http.post( '/checkout/intent', {
                    slugs:          [ this.productSlug ],
                    contact:        this.contactPayload,
                    discount_cents: this.credits.discount_cents || 0,
                });
                const { status, data } = response?.data || {};

                if( status !== 'success' )
                    throw new Error( data?.message || 'Order creation failed' );

                if( data.payment?.type !== 'completed' )
                    throw new Error( 'Unexpected response — expected completed type' );

                window.location.href = `${this.baseURL}/checkout/success/${data.hash}`;

            } catch( err ) {

                // 422 - баланс кредитов изменился, перезапрашиваем init
                if( err?.response?.status === 422 ) {
                    await this.handleCreditsChanged();
                    return;
                }

                this.globalError = err?.response?.data?.data?.message
                    || err?.response?.data?.message
                    || err.message
                    || 'Failed to apply credits';

                this.isLoading = false;
            }
        },

        /**
         * Кредиты изменились между загрузкой и сабмитом (бэкенд вернул 422).
         * Перезапрашиваем init - обновятся credits/total/discount_cents в UI,
         * пользователь видит актуальные суммы и подтверждает заново.
         */
        async handleCreditsChanged() {
            this.globalError = 'Your credits balance changed — amounts were updated. Please review and confirm again.';
            await this.init();
            this.isLoading = false;
        },

        validate() {

            const e = {};
            const f = this.form;
            const req = v => !v || !v.trim();

            if( req( f.email ) || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( f.email ) )
                e.email = true;

            this.errors = e;
            return Object.keys( e ).length === 0;
        },

        onPaymentError( message ) {
            this.globalError = message || 'Payment failed';
        },

        onPaymentLoading( isLoading ) {
            this.isLoading = isLoading;
        },
    },
};
</script>